import * as React from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

function StatList({ className, ...props }: React.ComponentProps<'dl'>) {
  return (
    <dl
      data-slot="stat-list"
      className={cn('grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] border-y', className)}
      {...props}
    />
  )
}

function Stat({
  label,
  value,
  description,
  className,
  ...props
}: Omit<React.ComponentProps<'div'>, 'children'> & {
  label: React.ReactNode
  value: React.ReactNode
  description?: React.ReactNode
}) {
  return (
    <div
      data-slot="stat"
      className={cn('min-w-0 border-r px-3 py-2 last:border-r-0', className)}
      {...props}
    >
      <dt className="truncate text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-lg font-semibold tabular-nums">{value}</dd>
      {description && <dd className="truncate text-xs text-muted-foreground">{description}</dd>}
    </div>
  )
}

/**
 * A headline number on its own card, after the DS Stat: the numeral is set in
 * the expanded display face and carries the card, so the label stays small and
 * muted and there is no header/body split leaving a gap above the number.
 * `Stat` is the dense inline version for a strip of figures.
 */
function StatCard({
  label,
  value,
  description,
  icon: Icon,
  className,
  ...props
}: Omit<React.ComponentProps<'div'>, 'children'> & {
  label: React.ReactNode
  value: React.ReactNode
  description?: React.ReactNode
  icon?: LucideIcon
}) {
  return (
    <div
      data-slot="stat-card"
      className={cn(
        'bg-card text-card-foreground flex min-w-0 flex-col gap-2 rounded-2xl border p-4 md:p-5',
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-2 text-sm font-medium text-muted-foreground">
        {/* Wraps rather than truncates: two-up on a phone, "Total Consignments" does not fit. */}
        <span className="min-w-0 leading-tight">{label}</span>
        {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
      </div>
      {/* Proportional figures: Mona Sans slashes its tabular zero, and a lone
          headline number has no column to line up with. */}
      <p className="font-display truncate text-3xl font-semibold leading-none">{value}</p>
      {description && <p className="truncate text-xs text-muted-foreground">{description}</p>}
    </div>
  )
}

/**
 * A labelled value that is not a headline number - a customer, a date, a
 * reference - on its own card. Same shape as StatCard, with body-size text
 * that wraps instead of a display numeral that truncates.
 */
function FactCard({
  label,
  value,
  icon: Icon,
  valueClassName,
  className,
  ...props
}: Omit<React.ComponentProps<'div'>, 'children'> & {
  label: React.ReactNode
  value: React.ReactNode
  icon?: LucideIcon
  valueClassName?: string
}) {
  return (
    <div
      data-slot="fact-card"
      className={cn(
        'bg-card text-card-foreground flex min-w-0 flex-col gap-1.5 rounded-2xl border p-4 md:p-5',
        className,
      )}
      {...props}
    >
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        {Icon && <Icon aria-hidden className="size-4 shrink-0" />}
        {label}
      </p>
      <div className={cn('break-words text-base font-semibold', valueClassName)}>{value}</div>
    </div>
  )
}

export { FactCard, Stat, StatCard, StatList }
